import {Component, type ComponentEvents, type ComponentOptions} from "./Component.js";

/**
 * A component that owns children. Children are attached to the container's event chain, so
 * events bubble from any leaf up to the application shell.
 */
export class Container<M extends ComponentEvents = ComponentEvents> extends Component<M> {
    readonly #children: Component<ComponentEvents>[] = [];

    constructor(options: ComponentOptions = {}) {
        super(options);
    }

    get children(): readonly Component<ComponentEvents>[] {
        return this.#children;
    }

    appendChild<TChild extends Component<ComponentEvents>>(child: TChild): TChild {
        this.#adopt(child);
        this.#children.push(child);
        this.node.appendChild(child.node);
        return child;
    }

    insertBefore<TChild extends Component<ComponentEvents>>(child: TChild, reference: Component<ComponentEvents>): TChild {
        const index = this.#children.indexOf(reference);
        if (index < 0) {
            return this.appendChild(child);
        }
        this.#adopt(child);
        this.#children.splice(index, 0, child);
        this.node.insertBefore(child.node, reference.node);
        return child;
    }

    replaceChild<TChild extends Component<ComponentEvents>>(newChild: TChild, oldChild: Component<ComponentEvents>): TChild {
        const index = this.#children.indexOf(oldChild);
        if (index < 0) {
            return this.appendChild(newChild);
        }
        this.#adopt(newChild);
        this.#children.splice(index, 1, newChild);
        this.node.replaceChild(newChild.node, oldChild.node);
        oldChild.nextListenable = undefined;
        return newChild;
    }

    removeChild(child: Component<ComponentEvents>): void {
        const index = this.#children.indexOf(child);
        if (index < 0) {
            return;
        }
        this.#children.splice(index, 1);
        this.node.removeChild(child.node);
        child.nextListenable = undefined;
    }

    removeAllChildren(): void {
        for (const child of [...this.#children]) {
            this.removeChild(child);
        }
    }

    destroyAllChildren(): void {
        for (const child of [...this.#children]) {
            this.removeChild(child);
            child.destroy();
        }
    }

    override async whenReady(): Promise<void> {
        await super.whenReady();
        await Promise.all(this.#children.map(child => child.whenReady()));
    }

    override destroy(): void {
        if (this.destroyed) {
            return;
        }
        this.destroyAllChildren();
        super.destroy();
    }

    #adopt(child: Component<ComponentEvents>): void {
        child.nextListenable = this;
    }
}
